import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';

interface ClickRow { id: string; slug: string | null; source: string; referrer: string | null; created_at: string; }

const chartConfig = { count: { label: 'Clicks', color: 'hsl(var(--primary))' } };

const ClicksView = ({ rows }: { rows: ClickRow[] }) => {
  const map = new Map<string, number>();
  rows.forEach(r => {
    const day = new Date(r.created_at).toISOString().slice(0, 10);
    map.set(day, (map.get(day) ?? 0) + 1);
  });
  const series = Array.from({ length: 30 }, (_, i) => {
    const day = new Date(Date.now() - (29 - i) * 86400000).toISOString().slice(0, 10);
    return { day: day.slice(5), count: map.get(day) ?? 0 };
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle>{rows.length} clicks</CardTitle><CardDescription>Last 30 days</CardDescription></CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} className="h-[220px] w-full">
            <BarChart data={series}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="day" tickLine={false} axisLine={false} interval={4} fontSize={11} />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={28} fontSize={11} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="count" fill="var(--color-count)" radius={3} />
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Post</TableHead><TableHead>Referrer</TableHead><TableHead className="w-44">When</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">No clicks recorded yet</TableCell></TableRow>}
            {rows.slice(0, 200).map(r => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.slug ?? '—'}</TableCell>
                <TableCell className="text-sm text-muted-foreground break-all">{r.referrer || 'direct'}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{new Date(r.created_at).toLocaleString('en-AU')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
};

const AdminClicks = () => {
  const [rows, setRows] = useState<ClickRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const since = new Date(Date.now() - 30 * 86400000).toISOString();
      const { data } = await supabase
        .from('blog_click_events')
        .select('id, slug, source, referrer, created_at')
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(2000);
      setRows(data || []);
      setLoading(false);
    })();
  }, []);

  const by = (s: string) => rows.filter(r => r.source === s);

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-5xl">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Blog Clicks</h1>
          <p className="text-muted-foreground mt-1">Where your blog traffic comes from</p>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <Tabs defaultValue="gbp">
            <TabsList>
              <TabsTrigger value="gbp">Google Business ({by('gbp').length})</TabsTrigger>
              <TabsTrigger value="organic">Organic ({by('organic').length})</TabsTrigger>
              <TabsTrigger value="other">Other ({by('other').length})</TabsTrigger>
            </TabsList>
            <TabsContent value="gbp"><ClicksView rows={by('gbp')} /></TabsContent>
            <TabsContent value="organic"><ClicksView rows={by('organic')} /></TabsContent>
            <TabsContent value="other"><ClicksView rows={by('other')} /></TabsContent>
          </Tabs>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminClicks;
